import { useEffect, useRef } from "react";

/**
 * CandlestickBackground
 *
 * Decorative, procedural candlestick chart that scrolls left-to-right behind
 * the landing hero. Zero market data, zero deps. Pure canvas.
 *
 * Design contract
 * - Not an interactive chart. Purely aesthetic.
 * - Loop duration ~38s (institutional pace, not frantic).
 * - Muted institutional green/red, low opacity so hero copy stays crisp.
 * - Runs at ~30fps to keep CPU low for a background layer.
 * - Respects prefers-reduced-motion: renders a single static frame.
 * - Pauses when tab is hidden (visibilitychange) to save cycles.
 * - No layout shift: absolute positioned, explicit size from parent.
 *
 * Perf notes
 * - DPR is capped at 2 so a 3x retina display doesn't allocate 9x pixels.
 * - We do NOT clear+redraw every frame; the loop advances a phase pointer
 *   and repaints only when advancing to a new frame slot.
 */
export default function CandlestickBackground({
    className = "",
    // Bright-on-navy institutional palette — luminous but not neon.
    greenColor = "#4ADE80",
    redColor = "#F87171",
    // Higher alpha on dark bg — chart reads as a real presence.
    baseAlpha = 0.55,
    // Glow radius in device pixels — subtle dimensionality, not a nightclub.
    glowBlurPx = 6,
    // Loop period in ms.
    loopMs = 38000,
    // Target frames per second — decorative content doesn't need 60.
    targetFps = 30,
    // Desktop candle count; mobile picks a smaller value from useResize.
    desktopCount = 60,
    mobileCount = 26,
    // Below this width we render a static frame regardless of reduce-motion.
    // Very small screens can jank on RAF + full-viewport canvas.
    staticBelowPx = 480,
}) {
    const canvasRef = useRef(null);
    const rafRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // ── Reduced-motion + very-small-screen static branch ──────────
        const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
        let staticMode = mql.matches || window.innerWidth < staticBelowPx;

        // ── Sizing (DPR-aware, cap DPR at 2) ──────────────────────────
        function sizeCanvas() {
            const parent = canvas.parentElement;
            if (!parent) return { w: 0, h: 0 };
            const rect = parent.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = Math.max(1, Math.floor(rect.width * dpr));
            canvas.height = Math.max(1, Math.floor(rect.height * dpr));
            canvas.style.width = rect.width + "px";
            canvas.style.height = rect.height + "px";
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            return { w: rect.width, h: rect.height };
        }

        // ── Procedural candle series ─────────────────────────────────
        // Deterministic-ish PRNG so re-mounts look consistent across renders.
        function mulberry32(a) {
            return function () {
                a |= 0;
                a = (a + 0x6d2b79f5) | 0;
                let t = Math.imul(a ^ (a >>> 15), 1 | a);
                t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
                return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
            };
        }

        function buildSeries(count) {
            const rnd = mulberry32(20260219);
            const series = [];
            // Overall upward drift across the whole width:
            // baseline moves from ~0.75 (lower) to ~0.30 (higher) top-relative.
            for (let i = 0; i < count; i++) {
                const t = i / (count - 1);
                const drift = 0.75 - t * 0.45; // lower-left → upper-right
                const wiggle = (rnd() - 0.5) * 0.12; // per-candle noise
                const midY = Math.max(0.1, Math.min(0.9, drift + wiggle));

                // ~55% green / 45% red — biased green to keep upward feel.
                const isGreen = rnd() < 0.55;

                // Candle body height as fraction of container height.
                const bodyH = 0.03 + rnd() * 0.06;
                // Wick length above/below body.
                const wickTop = 0.01 + rnd() * 0.035;
                const wickBot = 0.01 + rnd() * 0.035;

                series.push({ midY, isGreen, bodyH, wickTop, wickBot });
            }
            return series;
        }

        // ── Paint one frame given progress in [0, 1) ─────────────────
        function paint(progress, w, h, series, revealCount) {
            ctx.clearRect(0, 0, w, h);

            const n = series.length;
            // Column layout: leave a small gutter left+right.
            const gutter = 12;
            const colW = (w - gutter * 2) / n;
            const bodyW = Math.max(2, Math.min(10, colW * 0.55));

            // Global alpha ceiling for the whole layer.
            ctx.globalAlpha = baseAlpha;
            ctx.lineWidth = 1;

            const visible = Math.min(revealCount, n);
            for (let i = 0; i < visible; i++) {
                const c = series[i];
                const cx = gutter + colW * (i + 0.5);
                const midY = c.midY * h;
                const bodyH = c.bodyH * h;
                const top = midY - bodyH / 2;
                const bot = midY + bodyH / 2;

                // Per-candle age (0 = newest visible, 1 = oldest).
                const age = visible <= 1 ? 0 : 1 - i / (visible - 1);

                // Newest 1–2 candles get subtle wick flicker.
                let flick = 0;
                if (!staticMode && i >= visible - 2) {
                    // Very small pixel jitter, phase-locked to `progress` so it
                    // doesn't look like an epileptic seizure.
                    flick = Math.sin(progress * Math.PI * 8 + i) * 0.6;
                }

                const wickTopY = top - c.wickTop * h + flick;
                const wickBotY = bot + c.wickBot * h - flick;
                const color = c.isGreen ? greenColor : redColor;

                // Subtle color-matched glow on the body only (wicks stay crisp).
                // shadowBlur is in device pixels, so cheap on the GPU here.
                ctx.strokeStyle = color;
                ctx.shadowBlur = 0;
                ctx.beginPath();
                ctx.moveTo(Math.round(cx) + 0.5, wickTopY);
                ctx.lineTo(Math.round(cx) + 0.5, wickBotY);
                ctx.stroke();

                // Body — with glow
                ctx.fillStyle = color;
                ctx.shadowColor = color;
                ctx.shadowBlur = glowBlurPx;
                ctx.fillRect(
                    Math.round(cx - bodyW / 2),
                    Math.round(top),
                    Math.round(bodyW),
                    Math.max(1, Math.round(bodyH)),
                );
                ctx.shadowBlur = 0;

                // Fade the very newest candle in slightly for a subtle "just drawn" feel.
                if (!staticMode && age < 0.05) {
                    ctx.globalAlpha = baseAlpha * (0.5 + age * 10);
                } else {
                    ctx.globalAlpha = baseAlpha;
                }
            }
            ctx.globalAlpha = 1;
            ctx.shadowBlur = 0;
        }

        let series = [];
        let cachedSize = { w: 0, h: 0 };
        let count = window.innerWidth < 768 ? mobileCount : desktopCount;

        function rebuild() {
            cachedSize = sizeCanvas();
            count = window.innerWidth < 768 ? mobileCount : desktopCount;
            series = buildSeries(count);
        }

        rebuild();

        // ── Static branch: paint once, done. ─────────────────────────
        if (staticMode) {
            paint(1, cachedSize.w, cachedSize.h, series, series.length);
        } else {
            // ── Animated branch: RAF loop, throttled to targetFps ────
            const frameInterval = 1000 / targetFps;
            let lastFrameTime = performance.now();
            const loopStart = performance.now();

            const tick = (now) => {
                if (now - lastFrameTime >= frameInterval) {
                    lastFrameTime = now;
                    const elapsed = (now - loopStart) % loopMs;
                    const progress = elapsed / loopMs;
                    // Reveal candles progressively left→right, then hold.
                    // Reveal phase: 0 → 0.85 of the loop. Then a brief hold.
                    const revealPhase = Math.min(progress / 0.85, 1);
                    const revealCount = Math.max(
                        1,
                        Math.floor(revealPhase * series.length),
                    );
                    paint(
                        progress,
                        cachedSize.w,
                        cachedSize.h,
                        series,
                        revealCount,
                    );
                }
                rafRef.current = requestAnimationFrame(tick);
            };
            rafRef.current = requestAnimationFrame(tick);
        }

        // ── Resize handling (debounced-lite) ─────────────────────────
        let resizeTimer = 0;
        const onResize = () => {
            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                // Recheck static breakpoint on resize.
                const nextStatic =
                    mql.matches || window.innerWidth < staticBelowPx;
                if (nextStatic !== staticMode) {
                    staticMode = nextStatic;
                    cancelAnimationFrame(rafRef.current);
                    rebuild();
                    if (staticMode) {
                        paint(1, cachedSize.w, cachedSize.h, series, series.length);
                    } else {
                        // Restart the animated branch cleanly.
                        const frameInterval = 1000 / targetFps;
                        let lastFrameTime = performance.now();
                        const loopStart = performance.now();
                        const tick = (now) => {
                            if (now - lastFrameTime >= frameInterval) {
                                lastFrameTime = now;
                                const elapsed = (now - loopStart) % loopMs;
                                const progress = elapsed / loopMs;
                                const revealPhase = Math.min(progress / 0.85, 1);
                                const revealCount = Math.max(
                                    1,
                                    Math.floor(revealPhase * series.length),
                                );
                                paint(
                                    progress,
                                    cachedSize.w,
                                    cachedSize.h,
                                    series,
                                    revealCount,
                                );
                            }
                            rafRef.current = requestAnimationFrame(tick);
                        };
                        rafRef.current = requestAnimationFrame(tick);
                    }
                } else {
                    rebuild();
                    if (staticMode) {
                        paint(1, cachedSize.w, cachedSize.h, series, series.length);
                    }
                }
            }, 120);
        };
        window.addEventListener("resize", onResize, { passive: true });

        // Pause on tab hidden to save CPU.
        const onVisibility = () => {
            if (document.hidden) {
                cancelAnimationFrame(rafRef.current);
            } else if (!staticMode) {
                // Just re-kick the RAF; the timestamp math handles phase.
                const frameInterval = 1000 / targetFps;
                let lastFrameTime = performance.now();
                const loopStart = performance.now();
                const tick = (now) => {
                    if (now - lastFrameTime >= frameInterval) {
                        lastFrameTime = now;
                        const elapsed = (now - loopStart) % loopMs;
                        const progress = elapsed / loopMs;
                        const revealPhase = Math.min(progress / 0.85, 1);
                        const revealCount = Math.max(
                            1,
                            Math.floor(revealPhase * series.length),
                        );
                        paint(
                            progress,
                            cachedSize.w,
                            cachedSize.h,
                            series,
                            revealCount,
                        );
                    }
                    rafRef.current = requestAnimationFrame(tick);
                };
                rafRef.current = requestAnimationFrame(tick);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);

        // React to reduce-motion changes while page is open.
        const onReduceMotionChange = () => {
            onResize();
        };
        try {
            mql.addEventListener("change", onReduceMotionChange);
        } catch {
            // Safari < 14 fallback
            mql.addListener(onReduceMotionChange);
        }

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener("resize", onResize);
            document.removeEventListener("visibilitychange", onVisibility);
            try {
                mql.removeEventListener("change", onReduceMotionChange);
            } catch {
                mql.removeListener(onReduceMotionChange);
            }
            window.clearTimeout(resizeTimer);
        };
    }, [
        baseAlpha,
        greenColor,
        redColor,
        loopMs,
        targetFps,
        desktopCount,
        mobileCount,
        staticBelowPx,
        glowBlurPx,
    ]);

    return (
        <div
            aria-hidden="true"
            data-testid="hero-candlestick-bg"
            className={
                "pointer-events-none absolute inset-0 overflow-hidden " +
                className
            }
        >
            <canvas ref={canvasRef} className="block w-full h-full" />
        </div>
    );
}
